import { createSignal, type Accessor } from 'solid-js';
import type { QueryClient } from '@tanstack/solid-query';

import type {
  FxBreakline,
  FxBreaklineType,
} from './fx-workspace-breakline.types';
import { fxWorkspaceBreaklinesQueryKey } from './fx-workspace-query';
import { createWorkspaceLoadTokens } from './fx-workspace-scope';

export interface FxWorkspaceBreaklineStore {
  readonly workspaceBreaklines: Accessor<
    Record<string, ReadonlyMap<FxBreaklineType, FxBreakline>>
  >;
  readonly loadedBreaklineWorkspaceIds: Accessor<ReadonlySet<string>>;
  bumpBreaklineLoadToken(workspaceId: string): number;
  isLatestBreaklineLoadToken(workspaceId: string, token: number): boolean;
  setWorkspaceBreaklineMap(
    workspaceId: string,
    breaklines: readonly FxBreakline[]
  ): void;
  cacheWorkspaceBreaklines(
    workspaceId: string,
    breaklines: readonly FxBreakline[]
  ): void;
  markBreaklinesLoaded(workspaceId: string): void;
  appendWorkspaceBreakline(workspaceId: string, breakline: FxBreakline): void;
  clearWorkspace(workspaceId: string): void;
  resetRepositoryScope(): void;
}

export function createFxWorkspaceBreaklineStore(options: {
  queryClient: QueryClient;
  fetchKey: Accessor<string | undefined>;
}): FxWorkspaceBreaklineStore {
  const [workspaceBreaklines, setWorkspaceBreaklines] = createSignal<
    Record<string, ReadonlyMap<FxBreaklineType, FxBreakline>>
  >({});
  const [loadedBreaklineWorkspaceIds, setLoadedBreaklineWorkspaceIds] =
    createSignal<ReadonlySet<string>>(new Set());
  const breaklineLoadTokens = createWorkspaceLoadTokens();

  const bumpBreaklineLoadToken = (workspaceId: string): number =>
    breaklineLoadTokens.bump(workspaceId);

  const isLatestBreaklineLoadToken = (
    workspaceId: string,
    token: number
  ): boolean => breaklineLoadTokens.isLatest(workspaceId, token);

  const setWorkspaceBreaklineMap = (
    workspaceId: string,
    breaklines: readonly FxBreakline[]
  ): void => {
    const byType = new Map<FxBreaklineType, FxBreakline>(
      breaklines.map((breakline) => [breakline.type, breakline])
    );
    setWorkspaceBreaklines((previous) => ({
      ...previous,
      [workspaceId]: byType,
    }));
  };

  const cacheWorkspaceBreaklines = (
    workspaceId: string,
    breaklines: readonly FxBreakline[]
  ): void => {
    const key = options.fetchKey();
    if (key === undefined) {
      return;
    }

    options.queryClient.setQueryData(
      fxWorkspaceBreaklinesQueryKey(key, workspaceId),
      breaklines
    );
  };

  const markBreaklinesLoaded = (workspaceId: string): void => {
    setLoadedBreaklineWorkspaceIds(
      (previous) => new Set<string>([...previous, workspaceId])
    );
  };

  const appendWorkspaceBreakline = (
    workspaceId: string,
    breakline: FxBreakline
  ): void => {
    const existing = workspaceBreaklines()[workspaceId];
    const nextBreaklines = [
      ...(existing ? [...existing.values()] : []),
      breakline,
    ];
    setWorkspaceBreaklineMap(workspaceId, nextBreaklines);
    markBreaklinesLoaded(workspaceId);
    cacheWorkspaceBreaklines(workspaceId, nextBreaklines);
  };

  const clearWorkspace = (workspaceId: string): void => {
    setWorkspaceBreaklines((previous) => {
      const next = { ...previous };
      delete next[workspaceId];
      return next;
    });
    setLoadedBreaklineWorkspaceIds((previous) => {
      const next = new Set(previous);
      next.delete(workspaceId);
      return next;
    });
    breaklineLoadTokens.delete(workspaceId);
    const repositoryKey = options.fetchKey();
    if (repositoryKey !== undefined) {
      options.queryClient.removeQueries({
        queryKey: fxWorkspaceBreaklinesQueryKey(repositoryKey, workspaceId),
      });
    }
  };

  return {
    workspaceBreaklines,
    loadedBreaklineWorkspaceIds,
    bumpBreaklineLoadToken,
    isLatestBreaklineLoadToken,
    setWorkspaceBreaklineMap,
    cacheWorkspaceBreaklines,
    markBreaklinesLoaded,
    appendWorkspaceBreakline,
    clearWorkspace,
    resetRepositoryScope: (): void => {
      setWorkspaceBreaklines({});
      setLoadedBreaklineWorkspaceIds(new Set<string>());
      breaklineLoadTokens.clear();
    },
  };
}
